/* eslint-disable @typescript-eslint/no-use-before-define */
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { SpreedlySessionData } from './data/useSpreedlySession';

export type SpreedlyError = {
  key: string;
  message: string;
  attribute?: string;
};

export type SpreedlyPaymentMethod = {
  token: string;
  created_at: string;
  updated_at: string;
  email: string | null;
  data: Record<string, unknown> | null;
  storage_state: string;
  test: boolean;
  metadata: Record<string, unknown> | null;
  callback_url: string | null;
  last_four_digits: string;
  first_six_digits: string;
  card_type: string;
  first_name: string;
  last_name: string;
  month: number;
  year: number;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  phone_number: string | null;
  company: string | null;
  full_name: string;
  eligible_for_card_updater: boolean;
  shipping_address1: string | null;
  shipping_address2: string | null;
  shipping_city: string | null;
  shipping_state: string | null;
  shipping_zip: string | null;
  shipping_country: string | null;
  shipping_phone_number: string | null;
  issuer_identification_number: string;
  click_to_pay: boolean;
  managed: boolean;
  bin_metadata: {
    message: string;
  };
  subscribed_to_mastercard_abu: boolean;
  payment_method_type: string;
  errors: SpreedlyError[];
  fingerprint: string;
  verification_value: string;
  number: string;
};

export type SpreedlyValidationErrors = { [key: string]: string[] };

export type SpreedlyRequiredFields = {
  first_name: string;
  last_name: string;
  month: string;
  year: string;
};

/**
 * Spreedly field names for iframe inputs
 */
export type SpreedlyFieldName = 'number' | 'cvv';

/**
 * Spreedly field event types
 */
export type SpreedlyFieldEventType =
  | 'focus'
  | 'blur'
  | 'input'
  | 'mouseover'
  | 'mouseout'
  | 'tab';

/**
 * Spreedly field input properties returned with fieldEvent
 */
export type SpreedlyInputProperties = {
  cardType?: string;
  validNumber?: boolean;
  validCvv?: boolean;
  numberLength?: number;
  cvvLength?: number;
};

/**
 * Typed callback for Spreedly fieldEvent
 */
export type SpreedlyFieldEventCallback = (
  name: SpreedlyFieldName,
  eventType: SpreedlyFieldEventType,
  activeElement: SpreedlyFieldName | null,
  inputProperties: SpreedlyInputProperties
) => void;

/**
 * Spreedly SDK event callback arguments vary by event type:
 * - 'paymentMethod': (token: string, paymentMethod: SpreedlyPaymentMethod)
 * - 'errors': (errors: SpreedlyValidationErrors)
 * - 'ready': ()
 * - 'fieldEvent': (name: SpreedlyFieldName, type: SpreedlyFieldEventType, activeEl: SpreedlyFieldName | null, inputProperties: SpreedlyInputProperties)
 *
 * Using generic callback type since TypeScript doesn't support overloaded callback signatures well.
 */
type SpreedlyEventCallback = (...args: unknown[]) => void;

type SpreedlySDK = {
  init: (
    environmentKey: string,
    options: {
      numberEl: string;
      cvvEl: string;
      nonce: string;
      timestamp: number;
      certificateToken: string;
      signature: string;
    }
  ) => void;
  tokenizeCreditCard: (requiredFields: SpreedlyRequiredFields) => void;
  validate: (callback: (errors: SpreedlyValidationErrors) => void) => void;
  on: (event: string, callback: SpreedlyEventCallback) => void;
  off?: (event: string, callback: SpreedlyEventCallback) => void;
  unload?: () => void;
  /** Set the field type for iframe inputs (e.g., 'text', 'tel') */
  setFieldType?: (field: 'number' | 'cvv', type: 'text' | 'tel') => void;
  /** Set the number format for card number display */
  setNumberFormat?: (format: 'prettyFormat' | 'maskedFormat') => void;
  /** Apply CSS styles to iframe fields */
  setStyle?: (field: 'number' | 'cvv' | 'placeholder', css: string) => void;
};

export type UseSpreedlySDKParams = {
  sessionData: SpreedlySessionData | null | undefined;
  isLoading: boolean;
  environmentKey: string;
  onTokenizeSuccess?: (paymentMethod: SpreedlyPaymentMethod) => void;
  onTokenizeError?: (error: Error) => void;
  onValidationError?: (errors: SpreedlyValidationErrors) => void;
  onScriptLoadError?: (error: Error) => void;
  autoInitialize?: boolean; // Control when to initialize
  elementIds?: {
    cardNumber?: string;
    cvv?: string;
  };
};

/**
 * Field validity state tracked from Spreedly's fieldEvent inputProperties.
 * These values update in real-time as the user types in the iframe fields.
 */
export type SpreedlyFieldValidity = {
  validNumber: boolean;
  validCvv: boolean;
};

export type UseSpreedlySDKReturn = {
  isSDKReady: boolean;
  isInitialized: boolean;
  isTokenizing: boolean;
  fieldValidity: SpreedlyFieldValidity;
  cardNumberElRef: React.RefObject<HTMLDivElement>;
  cvvElRef: React.RefObject<HTMLDivElement>;
  tokenize: (requiredFields: SpreedlyRequiredFields) => Promise<void>;
  validate: () => Promise<SpreedlyValidationErrors | null>;
  scriptLoadError: Error | null;
  reinitialize: () => void;
};

/**
 * Spreedly iFrame Express SDK Script URL
 *
 * SECURITY NOTE: Subresource Integrity (SRI) is intentionally NOT used here.
 *
 * Why we don't use SRI for payment provider scripts:
 * 1. Payment providers (Spreedly, Stripe, Braintree) explicitly recommend against SRI
 *    because they need to deploy security patches immediately without breaking integrations.
 * 2. If the hash changes (due to security updates), payments would fail in production
 *    until we detect the change and deploy an updated hash.
 * 3. The iframe architecture already provides security isolation - card data is entered
 *    in Spreedly's iframe (hosted on core.spreedly.com) and never touches our JavaScript.
 * 4. Our PCI compliance scope is minimized by using their hosted fields approach.
 *
 * Mitigations in place:
 * - HTTPS only (enforced by the URL)
 * - Content Security Policy (CSP) should whitelist only core.spreedly.com for scripts
 * - Spreedly's iframe isolates sensitive card data from our application context
 *
 * @see https://docs.spreedly.com/reference/iframe/v1/
 */
const SPREEDLY_SCRIPT_URL = 'https://core.spreedly.com/iframe/iframe-v1.min.js';
const SPREEDLY_SCRIPT_ATTRIBUTE = 'data-spreedly';
const SDK_INIT_DELAY = 100; // ms to wait for DOM readiness

const DEFAULT_ELEMENT_IDS = {
  cardNumber: 'spreedly-number',
  cvv: 'spreedly-cvv',
} as const;

const getSpreedlySDK = (): SpreedlySDK | null => {
  if (typeof window !== 'undefined' && 'Spreedly' in window) {
    return (window as unknown as { Spreedly: SpreedlySDK }).Spreedly;
  }
  return null;
};

/**
 * Shared promise for script loading - ensures all callers share the same promise
 * and prevents memory leaks from duplicate event listeners.
 */
let spreedlyScriptPromise: Promise<void> | null = null;

const loadSpreedlyScript = (): Promise<void> => {
  // If SDK is already loaded, return resolved promise
  if (getSpreedlySDK()) {
    return Promise.resolve();
  }

  // If script is already loading, return the existing promise
  // This prevents adding duplicate event listeners
  if (spreedlyScriptPromise) {
    return spreedlyScriptPromise;
  }

  spreedlyScriptPromise = new Promise((resolve, reject) => {
    // Check if script element already exists (edge case: promise was reset but script exists)
    const existingScript = document.querySelector(
      `script[${SPREEDLY_SCRIPT_ATTRIBUTE}]`
    );

    if (existingScript) {
      // Use { once: true } to automatically remove listeners after they fire
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => {
          spreedlyScriptPromise = null; // Reset so it can be retried
          reject(new Error('Script load failed'));
        },
        { once: true }
      );
      return;
    }

    // Load Spreedly script
    const script = document.createElement('script');
    script.src = SPREEDLY_SCRIPT_URL;
    script.setAttribute(SPREEDLY_SCRIPT_ATTRIBUTE, 'true');
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      spreedlyScriptPromise = null; // Reset so it can be retried
      reject(new Error('Failed to load Spreedly script'));
    };
    document.head.appendChild(script);
  });

  return spreedlyScriptPromise;
};

/**
 * Type guard to validate Spreedly payment method response.
 * The `paymentMethod` parameter is intentionally typed as `unknown` because
 * this function receives unvalidated data from Spreedly's callback and must
 * verify its structure before it can be safely used as SpreedlyPaymentMethod.
 */
const isSpreedlyPaymentMethodValid = (
  token: string,
  paymentMethod: unknown
): paymentMethod is SpreedlyPaymentMethod =>
  typeof token === 'string' &&
  token.length > 0 &&
  paymentMethod !== null &&
  typeof paymentMethod === 'object' &&
  'token' in paymentMethod;

export const useSpreedlySDK = ({
  sessionData,
  isLoading,
  environmentKey,
  onTokenizeSuccess,
  onTokenizeError,
  onValidationError,
  onScriptLoadError,
  autoInitialize = true,
  elementIds,
}: UseSpreedlySDKParams): UseSpreedlySDKReturn => {
  const [isSDKReady, setIsSDKReady] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isTokenizing, setIsTokenizing] = useState(false);
  const [scriptLoadError, setScriptLoadError] = useState<Error | null>(null);
  const [fieldValidity, setFieldValidity] = useState<SpreedlyFieldValidity>({
    validNumber: false,
    validCvv: false,
  });

  const spreedlyRef = useRef<SpreedlySDK | null>(null);
  const cardNumberElRef = useRef<HTMLDivElement>(null);
  const cvvElRef = useRef<HTMLDivElement>(null);
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCleaningUpRef = useRef(false);

  // Memoize element IDs to prevent unnecessary re-renders
  const elementIdsConfig = useMemo(
    () => ({
      cardNumber: elementIds?.cardNumber ?? DEFAULT_ELEMENT_IDS.cardNumber,
      cvv: elementIds?.cvv ?? DEFAULT_ELEMENT_IDS.cvv,
    }),
    [elementIds]
  );

  useEffect(() => {
    if (isLoading) return;

    let isMounted = true;

    loadSpreedlyScript()
      .then(() => {
        if (!isMounted) return false;

        const sdk = getSpreedlySDK();
        if (sdk) {
          spreedlyRef.current = sdk;
          setIsSDKReady(true);
          setScriptLoadError(null);
        } else {
          const error = new Error(
            'Spreedly SDK failed to load. This may be due to Content Security Policy restrictions. Please ensure "core.spreedly.com" is added to the script-src directive.'
          );
          setScriptLoadError(error);
          onScriptLoadError?.(error);
          return false;
        }

        return true;
      })
      /* eslint-disable consistent-return */
      .catch((error: Error) => {
        if (isMounted) {
          const cspError = new Error(
            `Spreedly script blocked: ${error.message}. Please add "core.spreedly.com" to the script-src CSP directive.`
          );
          setScriptLoadError(cspError);
          onScriptLoadError?.(cspError);
        }

        return false;
      });

    return () => {
      isMounted = false;
    };
  }, [isLoading, onScriptLoadError]);

  const initializeSpreedly = useCallback(() => {
    if (!isSDKReady || !sessionData?.certificateToken) return;
    if (!cardNumberElRef.current || !cvvElRef.current) return;
    if (!environmentKey) {
      return;
    }

    const sdk = spreedlyRef.current;
    if (!sdk) return;

    // Clear any pending initialization
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
    }

    // Wait a bit for DOM to be ready
    initTimeoutRef.current = setTimeout(() => {
      try {
        sdk.init(environmentKey, {
          numberEl: elementIdsConfig.cardNumber,
          cvvEl: elementIdsConfig.cvv,
          nonce: sessionData.nonce,
          timestamp: sessionData.timestamp,
          certificateToken: sessionData.certificateToken,
          signature: sessionData.signature,
        });

        // Configure iframe styling and formatting when ready.
        // @see https://docs.spreedly.com/reference/iframe/v1/#setstyle
        // @see https://docs.spreedly.com/reference/iframe/v1/#setnumberformat
        sdk.on('ready', () => {
          // Field type must be 'text' or 'tel' for pretty formatting to work
          sdk.setFieldType?.('number', 'text');
          // Display card number with spaces as on physical card (e.g., "4111 1111 1111 1111")
          sdk.setNumberFormat?.('prettyFormat');

          // Style iframe inputs to match the form's native inputs.
          // Using Lato font (supported by Spreedly) and matching our design tokens:
          // - Font: Lato 16px/24px regular (#363940 text color)
          // - Height: 100% to fill the 48px container
          // - No padding (container provides px-xxs = 8px padding)
          const inputStyle = [
            'font-family: Lato, Arial, sans-serif',
            'font-size: 16px',
            'font-weight: 400',
            'line-height: 24px',
            'color: #363940',
            'width: 100%',
            'height: 100%',
            'border: none',
            'outline: none',
            'background: transparent',
          ].join('; ');

          sdk.setStyle?.('number', inputStyle);
          sdk.setStyle?.('cvv', inputStyle);

          // Style placeholder text to match native input placeholders
          sdk.setStyle?.('placeholder', 'color: #737373; font-weight: 400');
        });

        // Set up fieldEvent listener to track field validity in real-time
        const fieldValidityHandler: SpreedlyFieldEventCallback = (
          name,
          _eventType,
          _activeElement,
          inputProperties
        ) => {
          if (name === 'number' && inputProperties.validNumber !== undefined) {
            setFieldValidity(prev => ({
              ...prev,
              validNumber: inputProperties.validNumber ?? false,
            }));
          }
          if (name === 'cvv' && inputProperties.validCvv !== undefined) {
            setFieldValidity(prev => ({
              ...prev,
              validCvv: inputProperties.validCvv ?? false,
            }));
          }
        };

        sdk.on('fieldEvent', fieldValidityHandler as SpreedlyEventCallback);

        setIsInitialized(true);
      } catch (error) {
        const initError = new Error(
          `Spreedly initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
        onScriptLoadError?.(initError);
      }
    }, SDK_INIT_DELAY);
  }, [
    isSDKReady,
    sessionData,
    environmentKey,
    elementIdsConfig,
    onScriptLoadError,
    cardNumberElRef,
    cvvElRef,
  ]);

  // Auto-initialize if enabled
  useEffect(() => {
    if (autoInitialize && !isInitialized && !isCleaningUpRef.current) {
      initializeSpreedly();
    }

    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
    };
  }, [autoInitialize, isInitialized, initializeSpreedly]);

  // Cleanup on Unmount
  useEffect(
    () => () => {
      isCleaningUpRef.current = true;

      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }

      const sdk = spreedlyRef.current;
      if (sdk?.unload) {
        try {
          sdk.unload();
        } catch (error) {
          // Ignore
        }
      }
    },
    []
  );

  const tokenize = useCallback(
    async (requiredFields: SpreedlyRequiredFields): Promise<void> => {
      const sdk = spreedlyRef.current;

      if (!sdk || !isInitialized) {
        const error = new Error('Spreedly SDK is not initialized');
        onTokenizeError?.(error);
        return Promise.reject(error);
      }

      if (isTokenizing) {
        const error = new Error('Tokenization already in progress');
        onTokenizeError?.(error);
        return Promise.reject(error);
      }

      setIsTokenizing(true);

      return new Promise<void>((resolve, reject) => {
        let hasSettled = false;

        // Declare handlers first (will be assigned below)
        // paymentMethod is unknown - validated by isSpreedlyPaymentMethodValid type guard
        let paymentMethodHandler: (
          token: string,
          paymentMethod: unknown
        ) => void;
        let errorsHandler: (errors: SpreedlyValidationErrors) => void;
        let timeoutId: NodeJS.Timeout;

        // Define cleanup first so handlers can reference it
        const cleanup = () => {
          clearTimeout(timeoutId);
          if (sdk.off) {
            try {
              sdk.off('paymentMethod', paymentMethodHandler);
              sdk.off('errors', errorsHandler);
            } catch (e) {
              // Ignore
            }
          }
          setIsTokenizing(false);
        };

        // Timeout to prevent hanging if Spreedly SDK doesn't emit events
        timeoutId = setTimeout(() => {
          if (!hasSettled) {
            hasSettled = true;
            cleanup();
            const error = new Error(
              'Unable to create payment method. Please check your card details and try again.'
            );
            onTokenizeError?.(error);
            reject(error);
          }
        }, 30000); // 30 second timeout for tokenization

        // Now define the handlers that use cleanup
        paymentMethodHandler = (token: string, paymentMethod: unknown) => {
          if (hasSettled) return;
          hasSettled = true;
          cleanup();

          if (isSpreedlyPaymentMethodValid(token, paymentMethod)) {
            onTokenizeSuccess?.(paymentMethod);
            resolve();
          } else {
            const error = new Error(
              `Invalid payment method received: ${JSON.stringify({
                token,
                paymentMethod,
              })}`
            );
            onTokenizeError?.(error);
            reject(error);
          }
        };

        errorsHandler = (errors: SpreedlyValidationErrors) => {
          if (hasSettled) return;
          hasSettled = true;
          cleanup();

          const error = new Error(
            `Spreedly validation errors: ${JSON.stringify(errors)}`
          );
          onTokenizeError?.(error);
          reject(error);
        };

        sdk.on('paymentMethod', paymentMethodHandler);
        sdk.on('errors', errorsHandler);

        try {
          sdk.tokenizeCreditCard(requiredFields);
        } catch (err: unknown) {
          if (!hasSettled) {
            hasSettled = true;
            cleanup();
            const wrapped =
              err instanceof Error ? err : new Error('Tokenization failed');
            onTokenizeError?.(wrapped);
            reject(wrapped);
          }
        }
      });
    },
    [isInitialized, isTokenizing, onTokenizeSuccess, onTokenizeError]
  );

  const validate = useCallback((): Promise<SpreedlyValidationErrors | null> => {
    const sdk = spreedlyRef.current;
    if (!sdk || !isInitialized) {
      return Promise.resolve(null);
    }

    return new Promise(resolve => {
      let hasResolved = false;

      // Timeout to prevent hanging if Spreedly SDK doesn't call the callback
      const timeoutId = setTimeout(() => {
        if (!hasResolved) {
          hasResolved = true;
          // Resolve with null to let tokenization proceed (it will catch errors)
          resolve(null);
        }
      }, 10000); // 10 second timeout

      try {
        sdk.validate(errors => {
          if (hasResolved) return;
          hasResolved = true;
          clearTimeout(timeoutId);

          if (errors && Object.keys(errors).length > 0) {
            onValidationError?.(errors);
            resolve(errors);
          } else {
            resolve(null);
          }
        });
      } catch (error) {
        if (!hasResolved) {
          hasResolved = true;
          clearTimeout(timeoutId);
          // If validation throws, resolve with null and let tokenization handle errors
          resolve(null);
        }
      }
    });
  }, [isInitialized, onValidationError]);

  const reinitialize = useCallback(() => {
    setIsInitialized(false);
    initializeSpreedly();
  }, [initializeSpreedly]);

  return {
    isSDKReady,
    isInitialized,
    isTokenizing,
    fieldValidity,
    cardNumberElRef,
    cvvElRef,
    tokenize,
    validate,
    scriptLoadError,
    reinitialize,
  };
};

export default useSpreedlySDK;
